import { assert, BadRequestError, InternalError } from '@fragment/error-utils';
import type { SafeString } from '@fragment/types/strings';
import { SafeStringSchema } from '@fragment/types/strings';
import { parseWithError } from '@fragment/zod-utils/src';

import { base64Decode, base64Encode } from '../encode';

type BaseLedgerEntry = {
  ledgerId: string;
  id: string;
  ik?: SafeString;
  idv?: 0 | 1;
  // entry version in ledgerEntryV2Doc.version
  entryVersion: number;
};

type DecodedEntryId =
  | {
      // QLDB-originated Ledger Entries
      ledgerId: SafeString;
      // Use entryId instead of ik when looking up LedgerEntryV2
      entryId: SafeString;
      idv: 0;
      entryVersion: 1;
    }
  | {
      ledgerId: SafeString;
      entryId: SafeString;
      ik: SafeString;
      idv: 1;
      // This maps to ledgerEntryV2Doc.version
      entryVersion: number;
    };

export const ENTRY_ID_PREFIX = 'entry_' as const;
export const getEntryIk = (entry: {
  ik?: SafeString;
  id: string;
}): SafeString | undefined => {
  const { ik } = entry;
  return parseWithError(SafeStringSchema.optional(), ik, BadRequestError);
};

export const isDynamoEntry = (entry: BaseLedgerEntry) =>
  entry.idv === 1 && entry.id !== entry.ik;

export const encodeEntryId = (entry: BaseLedgerEntry) => {
  // During the QLDB migration, we are more forgiving and assuming that if idv
  // === 1, but ik is not set, then it's a QLDB entry that has been migrated.
  if (isDynamoEntry(entry)) {
    const ik = getEntryIk(entry);
    assert(ik, InternalError, {
      message: 'Tried to encode a dynamo entry id without an ik',
    });
    // ik:${line.ik} is <key:value> instead of <value:key > so that
    // both ends of the ID are random.
    // Additionally, we strip any trailing '=' from the e nd of the strings.
    // These are OK to remove, as b64 encoding will add t hese to pad the string
    // such that it's always a multiple of 4 ASCII chars.
    // https://stackoverflow.com/questions/9020409/is-it-ok-to-remove-the-equal-signs-from-a-base64-string

    assert(
      entry.entryVersion > 0 || entry.entryVersion === undefined,
      BadRequestError,
      {
        message: `Provided with an invalid ledger entry id, expected entryVersion to be greater than 0, got ${entry.entryVersion}\n`,
      }
    );
    const encoded =
      entry.entryVersion === 1 || entry.entryVersion === undefined
        ? base64Encode(
            `${entry.id}:en:${entry.ledgerId}:le:1:v:ik:${ik}`
          ).replace(/=/g, '')
        : base64Encode(
            `${entry.id}:en:${entry.ledgerId}:le:1:v:ik:${ik}:ev:${entry.entryVersion}`
          ).replace(/=/g, '');
    return `${ENTRY_ID_PREFIX}${encoded}`;
  }
  return base64Encode(`en:${entry.id}:le:${entry.ledgerId}`);
};

export const decodeEntryId = (encoded: string): DecodedEntryId => {
  if (encoded.startsWith(ENTRY_ID_PREFIX)) {
    const decoded = base64Decode(encoded.substring(ENTRY_ID_PREFIX.length));
    const bits = decoded.split(':');
    // Dynamo backed entry
    if (bits.length === 8 || bits.length === 10) {
      assert(bits[1] === 'en', BadRequestError, {
        message: `Provided with an invalid ledger entry id, expected 'en', got '${bits[1]}': ${encoded}`,
      });
      assert(bits[3] === 'le', BadRequestError, {
        message: `Provided with an invalid ledger entry id, expected 'le', got '${bits[3]}': ${encoded}`,
      });
      assert(bits[4] === '1', BadRequestError, {
        message: `Provided with an invalid ledger entry id, expected '1', got '${bits[4]}': ${encoded}`,
      });
      assert(bits[5] === 'v', BadRequestError, {
        message: `Provided with an invalid ledger entry id, expected 'v', got '${bits[5]}': ${encoded}`,
      });
      assert(bits[6] === 'ik', BadRequestError, {
        message: `Provided with an invalid ledger entry id, expected 'ik', got '${bits[6]}': ${encoded}`,
      });
      if (bits.length === 10) {
        assert(bits[8] === 'ev', BadRequestError, {
          message: `Provided with an invalid ledger entry id, expected 'ev', got '${bits[8]}': ${encoded}`,
        });
        // must be valid integer
        assert(Number.isInteger(Number(bits[9])), BadRequestError, {
          message: `Provided with an invalid ledger entry id, expected valid integer, got '${bits[9]}': ${encoded}`,
        });
      }
      return {
        entryId: parseWithError(SafeStringSchema, bits[0], BadRequestError),
        ledgerId: parseWithError(SafeStringSchema, bits[2], BadRequestError),
        ik: parseWithError(SafeStringSchema, bits[7], BadRequestError),
        idv: 1,
        entryVersion: bits.length === 10 ? parseInt(bits[9], 10) : 1,
      };
    }
  }
  const decoded = base64Decode(encoded);
  const bits = decoded.split(':');
  // QLDB backed entry

  assert(bits[0] === 'en', BadRequestError, {
    message: `Provided with an invalid ledger entry id: ${encoded}`,
  });
  assert(bits[2] === 'le', BadRequestError, {
    message: `Provided with an invalid ledger entry id: ${encoded}`,
  });
  return {
    entryId: parseWithError(SafeStringSchema, bits[1], BadRequestError),
    ledgerId: parseWithError(SafeStringSchema, bits[3], BadRequestError),
    idv: 0,
    entryVersion: 1,
  };
};

/**
 * Returns the `ik` for the LedgerEntryV2Doc from the decoded result.
 * This exists to handle the case where the `ik` is stored in the `entryId`
 * field for QLDB-originated Ledger Entries.
 */
export const getEntryIkFromDecoded = (decoded: DecodedEntryId): SafeString => {
  if (decoded.idv === 0) {
    assert(decoded.entryId, InternalError, {
      message: `Expected entryId to be set in decoded entry id for idv 0`,
    });
    return decoded.entryId;
  }
  assert(decoded.idv === 1, InternalError, {
    message: `Expected idv to be 1 in decoded entry id`,
  });
  assert(decoded.ik, InternalError, {
    message: `Expected ik to be set in decoded entry id for idv 1`,
  });
  return decoded.ik;
};

export const isEncodedEntryId = (maybeEncoded: string): boolean => {
  try {
    decodeEntryId(maybeEncoded);
    return true;
  } catch (e) {
    return false;
  }
};

export const encodeEntryLedgerId = (line: BaseLedgerEntry) =>
  base64Encode(`le:${line.ledgerId}`);
